export interface ServiceOrderAmountTotals {
  selectTotal: number;
  totalCreated: number;
  pending: number;
  approved: number;
  inProgress: number;
  concluded: number;
  canceled: number;
  rejected: number;
  inAnalising: number;
  pendingPayment: number;
  myServiceOrders: number;
}

const STATUS_AMOUNT_KEY: Record<string, keyof ServiceOrderAmountTotals> = {
  PENDENTE: 'pending',
  APROVADA: 'approved',
  'EM TESTE': 'inProgress',
  CONCLUIDA: 'concluded',
  CANCELADA: 'canceled',
  CANCELED: 'canceled',
  REPROVADA: 'rejected',
  REJECTED: 'rejected',
  'EM ANALISE': 'inAnalising',
  'PENDENTE DE PAGAMENTO': 'pendingPayment',
};

const STATISTICS_AMOUNT_FIELDS: Record<keyof ServiceOrderAmountTotals, string[]> = {
  selectTotal: ['selectTotalAmount', 'selectedAmount'],
  totalCreated: ['totalCreatedAmount', 'totalCreatedValue'],
  pending: ['pendingAmount', 'pendingValue'],
  approved: ['approvedAmount', 'approvedValue'],
  inProgress: ['inProgressAmount', 'inProgressValue', 'inTestAmount'],
  concluded: ['concludedAmount', 'concludedValue'],
  canceled: ['canceledAmount', 'canceledValue'],
  rejected: ['rejectedAmount', 'rejectedValue'],
  inAnalising: ['inAnalisingAmount', 'inAnalisingValue', 'inAnalysisAmount'],
  pendingPayment: ['pendingPaymentAmount', 'pendingPaymentValue'],
  myServiceOrders: ['myServiceOrdersAmount', 'myServiceOrdersValue'],
};

export function emptyServiceOrderAmountTotals(): ServiceOrderAmountTotals {
  return {
    selectTotal: 0,
    totalCreated: 0,
    pending: 0,
    approved: 0,
    inProgress: 0,
    concluded: 0,
    canceled: 0,
    rejected: 0,
    inAnalising: 0,
    pendingPayment: 0,
    myServiceOrders: 0,
  };
}

export function parseServiceOrderMoney(value: unknown): number {
  if (typeof value === 'number') return roundMoney(Number.isFinite(value) ? value : 0);
  if (value == null) return 0;

  let raw = String(value).trim().replace(/R\$/gi, '').replace(/\s/g, '');
  if (!raw) return 0;

  const hasComma = raw.includes(',');
  const hasDot = raw.includes('.');
  if (hasComma && hasDot) {
    raw = raw.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    raw = raw.replace(',', '.');
  } else if ((raw.match(/\./g) || []).length > 1) {
    raw = raw.replace(/\./g, '');
  }

  const numeric = Number(raw.replace(/[^\d.-]/g, ''));
  return roundMoney(Number.isFinite(numeric) ? numeric : 0);
}

export function formatServiceOrderAmount(value: number): string {
  const amount = roundMoney(Number.isFinite(value) ? value : 0);
  return amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function serviceOrderRowAmount(order: any): number {
  if (!order) return 0;
  if (order.costValue != null && order.costValue !== '') {
    return parseServiceOrderMoney(order.costValue);
  }
  return parseServiceOrderMoney(order.cost ?? order.price ?? 0);
}

export function sumServiceOrderAmounts(orders: any[] | null | undefined, userId?: string | null): ServiceOrderAmountTotals {
  const totals = emptyServiceOrderAmountTotals();

  (orders || []).forEach((order) => {
    const amount = serviceOrderRowAmount(order);
    totals.totalCreated = roundMoney(totals.totalCreated + amount);

    const key = STATUS_AMOUNT_KEY[normalizeStatus(order?.status)];
    if (key) {
      totals[key] = roundMoney(totals[key] + amount);
    }

    if (isMyOrder(order, userId)) {
      totals.myServiceOrders = roundMoney(totals.myServiceOrders + amount);
    }
  });

  totals.selectTotal = totals.totalCreated;
  return totals;
}

export function sumSelectedServiceOrderAmounts(items: any[] | null | undefined): number {
  return (items || []).reduce((sum, item) => roundMoney(sum + serviceOrderRowAmount(item)), 0);
}

export function amountForActiveFilter(
  amounts: ServiceOrderAmountTotals,
  onlyMyOrders: boolean,
  status: string | null,
): number {
  if (onlyMyOrders) return amounts.myServiceOrders || 0;

  const normalized = normalizeStatus(status);
  if (!normalized || normalized === 'TODOS' || normalized === 'ALL') {
    return amounts.totalCreated || 0;
  }

  const key = STATUS_AMOUNT_KEY[normalized];
  return key ? amounts[key] || 0 : amounts.totalCreated || 0;
}

export function amountsFromStatistics(stats: any): ServiceOrderAmountTotals | null {
  if (!stats || typeof stats !== 'object') return null;

  const present = (Object.keys(STATISTICS_AMOUNT_FIELDS) as Array<keyof ServiceOrderAmountTotals>)
    .some((key) => STATISTICS_AMOUNT_FIELDS[key].some((field) => stats[field] != null && stats[field] !== ''));
  if (!present) return null;

  const totals = emptyServiceOrderAmountTotals();
  (Object.keys(STATISTICS_AMOUNT_FIELDS) as Array<keyof ServiceOrderAmountTotals>).forEach((key) => {
    const field = STATISTICS_AMOUNT_FIELDS[key].find((name) => stats[name] != null && stats[name] !== '');
    totals[key] = field ? parseServiceOrderMoney(stats[field]) : 0;
  });

  if (!totals.selectTotal) {
    totals.selectTotal = totals.totalCreated;
  }

  return totals;
}

function isMyOrder(order: any, userId?: string | null): boolean {
  if (!userId) return false;
  const createdBy = order?.createdBy;
  const createdId = createdBy && typeof createdBy === 'object' ? createdBy.id : createdBy;
  return createdId === userId || order?.createdById === userId;
}

function normalizeStatus(status: unknown): string {
  return String(status || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

function roundMoney(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
